import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createReferenceValue, updateReferenceValue } from "../../api/reference";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { ReferenceTable } from "../../types/interfaces/admin";
import { ReferenceRow } from "../../types/model/reference";
import { getReferenceTableSchema } from "../../utils";

interface Props {
  open: boolean;
  onClose: () => void;
  tables: ReferenceTable[];
  schema: string;
  table: string;
  row?: ReferenceRow | null;
  fields: string[];
  onSaved: () => void;
}

const TableDialogForm: React.FC<Props> = ({
  open,
  onClose,
  tables,
  schema,
  table,
  row,
  fields,
  onSaved,
}) => {
  const [values, setValues] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();
  const handleError = useErrorHandler();

  useEffect(() => {
    const initial: Record<string, any> = {};
    fields.forEach((f) => {
      initial[f] = row?.[f as keyof ReferenceRow] ?? "";
    });
    setValues(initial);
  }, [row, fields]);

  const handleChange = (field: string, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const schemaValidation = getReferenceTableSchema(fields);
      await schemaValidation.validate(values, { abortEarly: false });

      if (row) {
        await updateReferenceValue(tables, schema, table, row.id, values);
      } else {
        await createReferenceValue(tables, schema, table, values);
      }

      onSaved();
      onClose();
    } catch (error) {
      handleError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {row
          ? t("admin.reference.table.edit-value")
          : t("admin.reference.table.add-value")}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2 }}>
        {fields.map((f) => (
          <TextField
            key={f}
            label={f}
            value={values[f] ?? ""}
            onChange={(e) => handleChange(f, e.target.value)}
            fullWidth
            margin="normal"
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("generic.cancel")}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? t("generic.saving") : t("generic.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TableDialogForm;
